/* src/app/StudioUIContext.tsx */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useAtom } from 'jotai';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { appSettingsAtom } from '../state/settings-atoms';

interface StudioUIContextValue {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const StudioUIContext = createContext<StudioUIContextValue | undefined>(undefined);

export function StudioUIProvider({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [, setSettings] = useAtom(appSettingsAtom);
  const theme = useResolvedTheme();

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const toggleTheme = () => {
    setSettings({
      themeMode: theme === 'light' ? 'dark' : 'light'
    });
  };

  const value: StudioUIContextValue = {
    sidebarOpen,
    toggleSidebar,
    setSidebarOpen,
    theme,
    toggleTheme
  };

  return <StudioUIContext.Provider value={value}>{children}</StudioUIContext.Provider>;
}

/** Access the Studio UI context for sidebar and theme state. Must be used within a StudioUIProvider. */
export function useStudioUI() {
  const context = useContext(StudioUIContext);
  if (context === undefined) {
    throw new Error('useStudioUI must be used within a StudioUIProvider');
  }
  return context;
}
